import { API_BASE_URL, BASE_URL } from '@synergy/config';
import { FormattedMessage } from 'react-intl';
import { UserRole } from '@synergy/models';
import { connect } from 'react-redux';
import { hasRole, padZeroLeft } from '@synergy/utils';
import urljoin from 'url-join';

import { AppState } from '../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../../autocomplete-list/pool-autocomplete-list';
import { Contact } from '../../../models/contact';
import { ContactApi } from '../../../api/contact-api';
import { PoolAutocompleteListFooter } from '../pool-autocomplete-list-footer';
import { PoolFormAutocompleteList } from '../../abstract/form-autocomplete-list';
import { ROUTES } from '../../../../features/sidebar-menu/navigation-drawer/routes';
import { User } from '../../../models/user';

interface PartialContact extends BaseAutocompleteSuggestionType {
    fullName: string;
    positionName: string;
}

interface PoolCustomerContactAutocompleteListProps {
    user: User;
    accountId?: number;
}

class PoolCustomerContactAutocompleteListBase extends PoolFormAutocompleteList<
    Contact,
    PartialContact,
    PoolCustomerContactAutocompleteListProps
> {
    private actionRoles = [UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales];
    private api = new ContactApi(API_BASE_URL);
    private apiAbortController?: AbortController;
    private searchParams = new URLSearchParams({ limit: '40' });

    public footer = hasRole(this.actionRoles, this.props.user) && (
        <PoolAutocompleteListFooter
            text="app.navigation.contacts"
            url={urljoin(BASE_URL, ROUTES.contactsAndOrganizations.contacts.new)}
        />
    );
    public label = (<FormattedMessage id="app.label.customer" />);

    public componentDidMount() {
        this.setState({ items: [] });
    }

    public getSuggestionValue = (suggestion: PartialContact) => suggestion && suggestion.fullName;

    public UNSAFE_componentWillReceiveProps(nextProps: PoolCustomerContactAutocompleteListProps) {
        if (this.props.accountId !== nextProps.accountId) {
            this.setState({ items: [] });
            this.searchParams.delete('accountId');
            if (nextProps.accountId) {
                this.searchParams.set('accountId', nextProps.accountId.toString());
            }
        }
    }

    public async handleValueChanged(search: string) {
        this.searchParams.set('search', search);
        this.apiAbortController?.abort();
        this.apiAbortController = new AbortController();
        const response = await this.api.search(this.searchParams, this.apiAbortController.signal);
        if (!response.success && response.requestCanceled) {
            return;
        }
        const items = response.success === true ? response.result.items : [];
        this.setState({ items });
    }

    public getPartial = (items: Contact[]): PartialContact[] => {
        return items.map(contact => {
            return {
                id: padZeroLeft(contact.id, 5),
                fullName: contact.fullName,
                tags: contact.getTags(30),
                positionName: contact.positionName || '',
            };
        });
    };
}

const mapStateToProps = (state: AppState) => ({
    user: state.user,
});

export const PoolCustomerContactAutocompleteList = connect(
    mapStateToProps,
    {}
)(PoolCustomerContactAutocompleteListBase);
